<template>
  <div class="geometry-relations-panel">
    <n-collapse :default-expanded-names="defaultExpanded" class="pm-collapse">
      <!-- 所选实体部分 -->
      <n-collapse-item title="所选实体" name="selected-entities">
        <div class="selected-entities-section">
          <SelectionListFrame
            :items="selectedEntityItems"
            :active-index="activeEntityIndex"
            list-role="list"
            :visible-rows="DEFAULT_VISIBLE_ENTITY_ROWS"
            item-key-field="objectId"
            empty-text="未选中任何实体"
            :context-menu-enabled="true"
            delete-label="取消选择"
            delete-all-label="取消全部选择"
            @update:active-index="handleActiveEntityIndexChange"
            @delete-item="handleDeleteSelectedEntity"
            @delete-all="handleDeleteAllSelectedEntities"
          >
            <template #item="{ item }">
              <div class="entity-main">
                <n-text>{{ resolveSelectedEntityName(item) }}</n-text>
              </div>
            </template>
          </SelectionListFrame>
        </div>
      </n-collapse-item>

      <!-- 现有几何关系部分 -->
      <n-collapse-item title="现有几何关系" name="existing-relations">
        <div class="existing-relations-section">
          <SelectionListFrame
            :items="existingRelationItems"
            :active-index="activeExistingRelationIndex"
            list-role="list"
            :visible-rows="DEFAULT_VISIBLE_RELATION_ROWS"
            item-key-field="relationId"
            empty-text=""
            @update:active-index="handleActiveExistingRelationIndexChange"
          >
            <template #item="{ item }">
              <div class="entity-main">
                <n-text>{{ toExistingRelationViewModel(item)?.label ?? '' }}</n-text>
              </div>
            </template>
          </SelectionListFrame>
        </div>
      </n-collapse-item>

      <!-- 可添加的几何关系部分 -->
      <n-collapse-item title="添加几何关系" name="add-relations">
        <div class="add-relations-section">
          <n-space  size="small" wrap>
            <n-button
              v-for="relation in addableRelationItems"
              :key="relation.kind"
              size="small"
              quaternary
              @click="handleAddRelationClick(relation)"
            >
              {{ relation.displayName }}
            </n-button>
          </n-space>
        </div>
      </n-collapse-item>
    </n-collapse>
  </div>
</template>

<script setup lang="ts">
import {computed, ref, toRaw} from 'vue';
import { storeToRefs } from 'pinia';
import {
  NCollapse,
  NCollapseItem,
  NButton,
  NSpace,
  NText,
} from 'naive-ui';
import SelectionListFrame from '@/components/editor/SelectionListFrame.vue';
import * as THREE from 'three';
import {
  SketchRelationCompatibility,
  type RelationCandidate,
  type SharedConstraintItem, ISketchObject,
} from '@puppycad/cad-core';
//------ 选择状态目录迁移 开始------
import { useGetMultiSelect } from '@/hooks/selection/useGetMultiSelect';
//------ 选择状态目录迁移 结束------
//------ 几何关系面板右键取消选择 开始------
import { useViewer } from '@/hooks/useViewer';
//------ 几何关系面板右键取消选择 结束------

/**
 * 几何关系面板组件。
 * 职责：
 * 1. 显示框选到的多个物体列表；
 * 2. 显示物体之间共有的几何关系；
 * 3. 提供添加几何关系的接口。
 */

/**
 * 现有关系视图模型。
 */
interface ExistingRelationViewModel {
  /** 关系 ID。 */
  relationId: string;
  /** 关系名称。 */
  label: string;
}

//------ 几何关系面板右键取消选择 开始------
/**
 * 所选实体视图模型。
 */
interface SelectedEntityItemViewModel {
  /** 对象 ID。 */
  objectId: string;
  /** 对象显示名称。 */
  name?: string;
}
//------ 几何关系面板右键取消选择 结束------


const DEFAULT_VISIBLE_ENTITY_ROWS = 4;
const DEFAULT_VISIBLE_RELATION_ROWS = 4;
const defaultExpanded = ['selected-entities', 'existing-relations', 'add-relations'];
const activeEntityIndex = ref<number>(-1);
const activeExistingRelationIndex = ref<number>(-1);
const relationCompatibility = new SketchRelationCompatibility();
//------ 几何关系面板右键取消选择 开始------
const { getViewer } = useViewer();
//------ 几何关系面板右键取消选择 结束------

const multiSelectStore = useGetMultiSelect();
const {
  multipleSelectedObjects,
} = storeToRefs(multiSelectStore);

/**
 * 将选中的对象转换为实体视图模型列表。
 */
const selectedEntityItems = computed(() => {
  const objects = (multipleSelectedObjects.value ?? []) as Array<{ uuid: string; name?: string }>;
  return objects.map((obj) => ({
    objectId: obj.uuid,
    name: obj.name,
  }));
});

/**
 * 将任意输入转换为所选实体视图模型。
 * @param item 列表项。
 * @returns 转换结果；失败时返回 null。
 */
function toSelectedEntityItemViewModel(item: unknown): SelectedEntityItemViewModel | null {
  if (!item || typeof item !== 'object') {
    return null;
  }

  const objectId = (item as { objectId?: unknown }).objectId;
  if (typeof objectId !== 'string' || objectId.length <= 0) {
    return null;
  }

  const nameValue = (item as { name?: unknown }).name;
  return {
    objectId,
    name: typeof nameValue === 'string' ? nameValue : undefined,
  };
}

//------ 几何关系面板右键取消选择 开始------
/**
 * 通过交互状态管理器同步场景选择状态。
 * @param objectIds 目标对象 ID 列表。
 */
function syncSelectionByObjectIds(objectIds: string[]): void {
  const viewer = getViewer();
  viewer?.interactionStateManager?.syncSelectionByObjectIds(objectIds);
}

/**
 * 处理“取消选择”右键动作。
 * @param payload 右键载荷。
 */
function handleDeleteSelectedEntity(payload: { item: unknown; index: number }): void {
  const removedEntity = toSelectedEntityItemViewModel(payload.item);
  if (!removedEntity) {
    return;
  }

  const nextObjectIds = selectedEntityItems.value
    .map((item) => item.objectId)
    .filter((objectId) => objectId !== removedEntity.objectId);

  syncSelectionByObjectIds(nextObjectIds);

  if (nextObjectIds.length <= 0) {
    activeEntityIndex.value = -1;
    return;
  }

  activeEntityIndex.value = Math.min(payload.index, nextObjectIds.length - 1);
}

/**
 * 处理“取消全部选择”右键动作。
 */
function handleDeleteAllSelectedEntities(): void {
  syncSelectionByObjectIds([]);
  activeEntityIndex.value = -1;
}
//------ 几何关系面板右键取消选择 结束------

function resolveSelectedEntityName(item: unknown): string {
  if (!item || typeof item !== 'object') {
    return '';
  }
  return String((item as { name?: string }).name ?? '');
}

/**

 * 计算“添加几何关系”候选列表。
 */
const addableRelationItems = computed<RelationCandidate[]>(() => {
  const selection = multipleSelectedObjects.value
  if (selection.length <= 0) {
    return [];
  }

  return relationCompatibility.getAvailableRelations(selection as unknown as ISketchObject[])
});

/**
 * 现有几何关系列表。
 * 通过 SketchRelationCompatibility.getSharedConstraints 获取所有选中对象共有的约束。
 */
const existingRelationItems = computed<ExistingRelationViewModel[]>(() => {
  const selection = multipleSelectedObjects.value;
  if (selection.length <= 0) {
    return [];
  }

  const sharedConstraints: SharedConstraintItem[] = relationCompatibility.getSharedConstraints(
    selection as unknown as ISketchObject[],
  );

  return sharedConstraints.map((item) => ({
    relationId: String(item.constraintId),
    label: item.displayName,
  }));
});

/**
 * 处理实体列表激活行变化。
 * @param index 激活行索引。
 */
function handleActiveEntityIndexChange(index: number): void {
  activeEntityIndex.value = index;
}

/**
 * 处理现有关系列表激活行变化。
 * @param index 激活行索引。
 */
function handleActiveExistingRelationIndexChange(index: number): void {
  activeExistingRelationIndex.value = index;
}

/**
 * 处理现有关系视图模型转换。
 * @param item 原始项目。
 * @returns 现有关系视图模型。
 */
function toExistingRelationViewModel(item: unknown): ExistingRelationViewModel | null {
  if (!item || typeof item !== 'object') {
    return null;
  }
  return item as ExistingRelationViewModel;
}

/**
 * 处理添加关系点击。
 * 规则：
 * 1. self 关系：对每个选中对象分别创建；
 * 2. multi 关系：按当前选择尝试创建一次多实体关系。
 * @param relation 关系候选。
 */
function handleAddRelationClick(relation: RelationCandidate): void {
  const viewer = getViewer();

  if (!viewer) {
    return;
  }

  viewer.sketchRelationCreationService.createRelation({
    objects: toRaw(multipleSelectedObjects.value) as THREE.Object3D[],
    relationKind: relation.kind,
  });
}

</script>

<style scoped>
.geometry-relations-panel {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
}

.selected-entities-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.entity-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.entity-type {
  font-size: 12px;
}

.existing-relations-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.add-relations-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

</style>
